import React, { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import Layout from "../components/Layout";
import { Modal } from "bootstrap";
import { useTranslation } from "react-i18next";
import { productApi } from "../api/productApi";
import { orderApi } from "../api/orderApi";
import ReCAPTCHA from "react-google-recaptcha";

export default function ProductDetail() {
  const { id } = useParams();
  const nav = useNavigate();
  const brandColor = "#002244";

  const { t, i18n } = useTranslation();
  const lang = (i18n?.language || "az").toLowerCase();

  const [product, setProduct] = useState(null);
  const [loading, setLoading] = useState(true);

  const [showAlert, setShowAlert] = useState(false);
  const [sending, setSending] = useState(false);
  const [err, setErr] = useState("");

  const [form, setForm] = useState({
    ad: "",
    soyad: "",
    email: "",
    telefon: "",
    qeyd: "",
  });

  // ✅ reCAPTCHA
  const recaptchaRef = useRef(null);
  const [captchaToken, setCaptchaToken] = useState("");
  const RECAPTCHA_SITE_KEY = process.env.REACT_APP_RECAPTCHA_SITE_KEY;

  // ✅ pick (nameAz/nameEn/nameRu, descAz...)
  const pick = (obj, base) => {
    const suffix = lang.startsWith("en") ? "En" : lang.startsWith("ru") ? "Ru" : "Az";
    const keyCamel = `${base}${suffix}`; // nameAz, descAz
    const keyPascal = `${base[0].toUpperCase() + base.slice(1)}${suffix}`; // NameAz
    const fallbackCamelAz = `${base}Az`;
    const fallbackPascalAz = `${base[0].toUpperCase() + base.slice(1)}Az`;
    return obj?.[keyCamel] ?? obj?.[keyPascal] ?? obj?.[fallbackCamelAz] ?? obj?.[fallbackPascalAz] ?? "";
  };

  // ✅ spec pick (keyAz/valueAz)
  const pickSpec = (s, base) => {
    const suffix = lang.startsWith("en") ? "En" : lang.startsWith("ru") ? "Ru" : "Az";
    const keyCamel = `${base}${suffix}`; // keyAz/valueAz
    const keyPascal = `${base[0].toUpperCase() + base.slice(1)}${suffix}`;
    const fallbackAz = `${base}Az`;
    return s?.[keyCamel] ?? s?.[keyPascal] ?? s?.[fallbackAz] ?? "";
  };

  useEffect(() => {
    let ok = true;

    const load = async () => {
      setLoading(true);
      try {
        const res = await productApi.get(id);
        if (!ok) return;
        setProduct(res.data || null);
      } catch {
        if (!ok) return;
        setProduct(null);
      } finally {
        if (!ok) return;
        setLoading(false);
      }
    };

    load();
    return () => {
      ok = false;
    };
  }, [id]);

  const handleChange = (e) => setForm((p) => ({ ...p, [e.target.name]: e.target.value }));

  const closeBootstrapModal = () => {
    const modalEl = document.getElementById("orderModal");
    if (!modalEl) return;
    const modal = Modal.getInstance(modalEl) || new Modal(modalEl);
    modal.hide();
  };

  const resetModalState = () => {
    setErr("");
    setCaptchaToken("");
    recaptchaRef.current?.reset();
  };

  const validate = () => {
    if (!form.ad.trim() || !form.soyad.trim() || !form.email.trim() || !form.telefon.trim()) {
      return t("order_required");
    }

    // email check (istəsən götür)
    if (!/^\S+@\S+\.\S+$/.test(form.email.trim())) {
      return t("contact_err_email_valid") || "Email düzgün deyil";
    }

    // captcha
    if (!captchaToken) return t("contact_err_captcha") || "Captcha təsdiqlənməyib";
    if (!RECAPTCHA_SITE_KEY) return "reCAPTCHA site key tapılmadı (.env yoxla)";

    return "";
  };

  const handleSend = async () => {
    if (!product) return;

    setErr("");
    const v = validate();
    if (v) {
      setErr(v);
      return;
    }

    setSending(true);
    try {
      await orderApi.create({
        productId: product.id,
        name: form.ad.trim(),
        surname: form.soyad.trim(),
        email: form.email.trim(),
        phone: form.telefon.trim(),
        note: form.qeyd?.trim() || null,

        captchaToken, // ✅ backend-ə göndər
      });

      setShowAlert(true);
      closeBootstrapModal();
      window.setTimeout(() => setShowAlert(false), 3000);

      setForm({ ad: "", soyad: "", email: "", telefon: "", qeyd: "" });
      resetModalState();
    } catch (e) {
      const msg = e?.response?.data || e?.message || t("order_send_fail");
      setErr(String(msg));

      // istəsən error-da da reset et:
      // resetModalState();
    } finally {
      setSending(false);
    }
  };

  // modal bağlananda captcha + error sıfırlansın (bootstrap event)
  useEffect(() => {
    const el = document.getElementById("orderModal");
    if (!el) return;

    const onHidden = () => resetModalState();
    el.addEventListener("hidden.bs.modal", onHidden);
    return () => el.removeEventListener("hidden.bs.modal", onHidden);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (loading) {
    return (
      <Layout>
        <section className="py-5">
          <div className="container">{t("loading")}</div>
        </section>
      </Layout>
    );
  }

  if (!product) {
    return (
      <Layout>
        <section className="py-5">
          <div className="container">
            <h2>{t("product_not_found")}</h2>
          </div>
        </section>
      </Layout>
    );
  }

  const hasImage = product?.hasImage ?? true;
  const imgUrl = hasImage ? productApi.getImageUrl(product.id) + `?t=${Date.now()}` : null;

  return (
    <Layout>
      <section className="py-5">
        <div className="container">
          {/* ALERT */}
          {showAlert && (
            <div className="alert alert-success fw-bold" role="alert">
              {t("order_sent_ok")}
            </div>
          )}

          <div className="row g-4 align-items-stretch">
            {/* SOL: ŞƏKİL col-5 */}
            <div className="col-12 col-md-5">
              <div
                className="rounded shadow-sm overflow-hidden"
                style={{
                  width: "100%",
                  height: 420,
                  background: "#f2f4f7",
                }}
              >
                {imgUrl ? (
                  <img
                    src={imgUrl}
                    alt={pick(product, "name")}
                    style={{
                      width: "100%",
                      height: "100%",
                      objectFit: "cover",
                      display: "block",
                    }}
                    onError={(e) => {
                      e.currentTarget.style.display = "none";
                    }}
                  />
                ) : (
                  <div
                    className="w-100 h-100 d-flex align-items-center justify-content-center"
                    style={{ fontWeight: 900, color: "rgba(0,0,0,.55)" }}
                  >
                    {t("no_image")}
                  </div>
                )}
              </div>
            </div>

            {/* SAĞ: MƏZMUN col-7 */}
            <div className="col-12 col-md-7">
              <div style={{ minHeight: 420, display: "flex", flexDirection: "column" }}>
                {/* TOP */}
                <div style={{ flex: 1 }}>
                  <div className="d-flex align-items-start justify-content-between gap-3 mb-3">
                    <h1 className="fw-bold mb-0" style={{ fontSize: "27px", lineHeight: 1.15 }}>
                      {pick(product, "name")}
                    </h1>

                    <div
                      className="fw-bold"
                      style={{
                        color: brandColor,
                        whiteSpace: "nowrap",
                        fontSize: 18,
                        padding: "6px 12px",
                        borderRadius: 10,
                        background: "rgba(0,34,68,0.06)",
                        border: "1px solid rgba(0,34,68,0.12)",
                      }}
                    >
                      {t("price_label")}
                    </div>
                  </div>

                  <p className="text-muted mb-4" style={{ fontSize: "17px", lineHeight: 1.6 }}>
                    {pick(product, "desc")}
                  </p>

                  {/* SPECS */}
                  <div className="mb-3">
                    {(product.specs || []).length > 0 ? (
                      (product.specs || []).map((s, idx) => (
                        <div
                          key={s.id ?? idx}
                          className="d-flex justify-content-between align-items-center mb-3 rounded"
                          style={{ background: "#f8f9fa", fontSize: "16px", padding: "10px 12px" }}
                        >
                          <strong>{pickSpec(s, "key")}</strong>
                          <span>{pickSpec(s, "value")}</span>
                        </div>
                      ))
                    ) : (
                      <div style={{ opacity: 0.65, fontWeight: 700 }} />
                    )}
                  </div>
                </div>

                {/* AŞAĞI BAR */}
                <div className="d-flex align-items-center justify-content-between mt-3 pt-3" style={{ borderTop: "1px solid rgba(0,0,0,0.08)" }}>
                  <button
                    className="btn text-white fw-bold"
                    data-bs-toggle="modal"
                    data-bs-target="#orderModal"
                    style={{
                      background: brandColor,
                      padding: "12px 22px",
                      borderRadius: 10,
                      fontSize: 16,
                    }}
                  >
                    {t("order_btn")}
                  </button>

                  <button type="button" className="btn btn-outline-secondary fw-bold" onClick={() => nav(-1)}>
                    {t("back")}
                  </button>
                </div>
              </div>
            </div>
          </div>

          {/* MODAL */}
          <div className="modal fade" id="orderModal" tabIndex="-1" aria-hidden="true">
            <div className="modal-dialog modal-dialog-centered">
              <div className="modal-content p-3">
                <div className="modal-header position-relative">
                  <h5 className="modal-title fw-bold w-100 text-center m-0" style={{ paddingRight: 40 }}>
                    {t("order_form_title")}
                  </h5>

                  <button
                    className="btn-close position-absolute end-0 me-2"
                    data-bs-dismiss="modal"
                    aria-label="Close"
                    disabled={sending}
                  />
                </div>

                <div className="modal-body">
                  {/* Error (modal daxilində) */}
                  {err && (
                    <div className="alert alert-danger d-flex align-items-center" role="alert">
                      <span className="me-2">⚠️</span>
                      <div>{err}</div>
                    </div>
                  )}

                  <div className="mb-3">
                    <label className="form-label">{t("lbl_name")}</label>
                    <input name="ad" className="form-control" value={form.ad} onChange={handleChange} disabled={sending} />
                  </div>

                  <div className="mb-3">
                    <label className="form-label">{t("lbl_surname")}</label>
                    <input name="soyad" className="form-control" value={form.soyad} onChange={handleChange} disabled={sending} />
                  </div>

                  <div className="mb-3">
                    <label className="form-label">{t("lbl_email")}</label>
                    <input
                      name="email"
                      type="email"
                      className="form-control"
                      value={form.email}
                      onChange={handleChange}
                      disabled={sending}
                    />
                  </div>

                  <div className="mb-3">
                    <label className="form-label">{t("lbl_phone")}</label>
                    <input name="telefon" className="form-control" value={form.telefon} onChange={handleChange} disabled={sending} />
                  </div>

                  <div className="mb-3">
                    <label className="form-label">{t("lbl_note")}</label>
                    <textarea name="qeyd" className="form-control" rows="3" value={form.qeyd} onChange={handleChange} disabled={sending} />
                  </div>

                  {/* ✅ reCAPTCHA */}
                  <div className="mb-2">
                    <ReCAPTCHA
                      ref={recaptchaRef}
                      sitekey={RECAPTCHA_SITE_KEY || ""}
                      onChange={(token) => setCaptchaToken(token || "")}
                      onExpired={() => setCaptchaToken("")}
                    />
                  </div>

                  {/* Debug istəsən (sonra sil) */}
                  {/* <div style={{ fontSize: 12, opacity: 0.6 }}>token: {captchaToken ? "var" : "yox"}</div> */}
                </div>

                <div className="modal-footer justify-content-center">
                  <button
                    className="btn text-white fw-bold"
                    onClick={handleSend}
                    disabled={sending || !captchaToken}
                    style={{
                      background: brandColor,
                      padding: "12px 22px",
                      borderRadius: 10,
                      fontSize: 16,
                      opacity: sending ? 0.75 : 1,
                    }}
                  >
                    {sending ? t("sending") : t("send")}
                  </button>
                </div>
              </div>
            </div>
          </div>
          {/* /MODAL */}
        </div>
      </section>
    </Layout>
  );
}
